import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Landing from './components/Landing/Landing';
import Dashboard from './components/Dashboard/Dashboard';
import EmpresaPortal from './components/EmpresaPortal/EmpresaPortal';
import TestView from './components/Test/TestView';
import ResultsView from './components/Results/ResultsView';
import Login from './components/Auth/Login';
import RegistroEmpresa from './components/Registro/RegistroEmpresa';
import VerificarCuenta from './components/Registro/VerificarCuenta';
import ConstructorPrueba from './components/EmpresaPortal/ConstructorPrueba';
import SolicitarDemo from './components/Demo/SolicitarDemo';
import AvisoCookies, { leerConsentimiento, guardarConsentimiento } from './components/Shared/AvisoCookies';

// Componente para proteger rutas
const ProtectedRoute = ({ children, setIsAuthenticated, setUsuario }) => {
    const [isAuthenticated, setIsAuthenticatedState] = useState(null);
    
    useEffect(() => {
        verificarAuth();
    }, []);
    
    const verificarAuth = async () => {
        try {
            const response = await fetch('/api/auth/verificar', {
                credentials: 'include'
            });
            const data = await response.json();
            const autenticado = data.autenticado || false;
            setIsAuthenticatedState(autenticado);
            if (setIsAuthenticated) setIsAuthenticated(autenticado);
            if (setUsuario && data.usuario) setUsuario(data.usuario);
        } catch (error) {
            console.error('Error verificando auth:', error);
            setIsAuthenticatedState(false);
            if (setIsAuthenticated) setIsAuthenticated(false);
        }
    };
    
    if (isAuthenticated === null) {
        return <div className="loading">Verificando autenticación...</div>;
    }
    
    return isAuthenticated ? children : <Navigate to="/login" />;
};

// Páginas públicas donde se mide tráfico y se muestra el aviso de cookies.
// El panel y /test (candidatos respondiendo una prueba) quedan fuera.
const RUTAS_PUBLICAS = ['/', '/login', '/registro', '/solicitar-demo'];

// Registra una visita en /api/trafico cada vez que cambia la ruta (para el
// recuadro de tráfico del dashboard admin), solo si el visitante aceptó las
// cookies. Solo manda el pathname, nunca el query string.
const RastreadorVisitas = () => {
    const location = useLocation();
    const [consentimiento, setConsentimiento] = useState(leerConsentimiento);
    const ultimaRuta = useRef(null);
    const esPublica = RUTAS_PUBLICAS.includes(location.pathname);

    useEffect(() => {
        if (!esPublica || consentimiento !== 'aceptadas') return;
        // Evita el doble registro de StrictMode en desarrollo
        if (ultimaRuta.current === location.pathname) return;
        ultimaRuta.current = location.pathname;

        fetch('/api/trafico/visita', {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ruta: location.pathname, referer: document.referrer || null })
        }).catch(() => { /* el tráfico nunca debe romper la navegación */ });
    }, [location.pathname, esPublica, consentimiento]);

    const aceptar = () => {
        guardarConsentimiento('aceptadas');
        setConsentimiento('aceptadas');
    };

    // Sin opción de rechazar: la barra se muestra hasta que se acepte (también
    // a quien la rechazó con la versión anterior, que guardaba 'rechazadas').
    return esPublica && consentimiento !== 'aceptadas' ? <AvisoCookies onAceptar={aceptar} /> : null;
};

function App() {
    const [usuario, setUsuario] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    useEffect(() => {
        verificarSesion();
    }, []);
    
    const verificarSesion = async () => {
        try {
            const response = await fetch('/api/auth/verificar', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.autenticado) {
                setUsuario(data.usuario);
                setIsAuthenticated(true);
            }
        } catch (error) {
            console.error('Error verificando sesión:', error);
        }
    };
    
    const handleLogin = (usuarioData) => {
        setUsuario(usuarioData);
        setIsAuthenticated(true);
    };
    
    const handleLogout = async () => {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
            setUsuario(null);
            setIsAuthenticated(false);
            window.location.href = '/login';
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
        }
    };
    
    return (
        <Router>
            <RastreadorVisitas />
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/registro" element={<RegistroEmpresa />} />
                <Route path="/solicitar-demo" element={<SolicitarDemo />} />
                <Route path="/verificar-cuenta/:token" element={<VerificarCuenta />} />
                <Route path="/test" element={<TestView />} />
                <Route
                    path="/panel"
                    element={
                        <ProtectedRoute setIsAuthenticated={setIsAuthenticated} setUsuario={setUsuario}>
                            {usuario?.rol === 'empresa'
                                ? <EmpresaPortal usuario={usuario} onLogout={handleLogout} />
                                : <Dashboard usuario={usuario} onLogout={handleLogout} />}
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/resultados/:id"
                    element={
                        <ProtectedRoute setIsAuthenticated={setIsAuthenticated} setUsuario={setUsuario}>
                            <ResultsView />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/panel/pruebas/nueva"
                    element={
                        <ProtectedRoute setIsAuthenticated={setIsAuthenticated} setUsuario={setUsuario}>
                            <ConstructorPrueba />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/panel/pruebas/:id/editar"
                    element={
                        <ProtectedRoute setIsAuthenticated={setIsAuthenticated} setUsuario={setUsuario}>
                            <ConstructorPrueba />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </Router>
    );
}

export default App;